import React, { useEffect, useMemo, useState } from "react";
import NewsCard from "component/news/NewsCard.jsx";
import { FaThLarge, FaList } from "react-icons/fa";
import { Link, useSearchParams } from "react-router-dom";
import useBreakpoint from "utils/useBreakPoint.js";
import AllNews from "component/news/AllNews.jsx";
import { NewsCardSkeleton, AllNewsSkeleton } from "component/news/NewsSkeletons.jsx";
import { getUserId } from "utils/auth.js";
import { getSelectedLanguage } from "utils/language";
import { useTranslation } from "react-i18next";
import {
  NEWS_PLACEHOLDER_URL,
  getNewsBranches,
  searchNews,
} from "modules/News/api";

const INLINE_PATTERNS = [
  { type: "link", regex: /\[([^\]]+)\]\(([^)]+)\)/ },
  { type: "code", regex: /`([^`]+)`/ },
  { type: "bold", regex: /\*\*(.+?)\*\*/ },
  { type: "bold", regex: /__(.+?)__/ },
  { type: "italic", regex: /\*(.+?)\*/ },
  { type: "italic", regex: /_(.+?)_/ },
];

const cloneRegex = (regex) => new RegExp(regex.source, regex.flags);

const tokenizeInline = (text, keyPrefix) => {
  if (!text) return [];
  const nodes = [];
  let remaining = text;
  let safety = 0;

  while (remaining && safety < 500) {
    safety += 1;
    let earliestMatch = null;
    let matchedPattern = null;

    for (const pattern of INLINE_PATTERNS) {
      const regex = cloneRegex(pattern.regex);
      const match = regex.exec(remaining);
      if (match && (earliestMatch === null || match.index < earliestMatch.index)) {
        earliestMatch = match;
        matchedPattern = pattern;
      }
    }

    if (!earliestMatch || !matchedPattern) {
      nodes.push(remaining);
      break;
    }

    if (earliestMatch.index > 0) {
      nodes.push(remaining.slice(0, earliestMatch.index));
    }

    const matchedText = earliestMatch[0];
    if (!matchedText) {
      break;
    }

    switch (matchedPattern.type) {
      case "bold":
        nodes.push({ type: "strong", content: earliestMatch[1] });
        break;
      case "italic":
        nodes.push({ type: "em", content: earliestMatch[1] });
        break;
      case "code":
        nodes.push({ type: "code", content: earliestMatch[1] });
        break;
      case "link":
        nodes.push({ type: "link", content: earliestMatch[1], url: earliestMatch[2] });
        break;
      default:
        nodes.push(matchedText);
    }

    remaining = remaining.slice(earliestMatch.index + matchedText.length);
  }

  if (safety >= 500) {
    nodes.push(remaining);
  }

  return nodes.map((node, idx) => {
    const key = `${keyPrefix}-inline-${idx}`;
    if (typeof node === "string") {
      return <React.Fragment key={key}>{node}</React.Fragment>;
    }
    switch (node.type) {
      case "strong":
        return <strong key={key}>{node.content}</strong>;
      case "em":
        return <em key={key}>{node.content}</em>;
      case "code":
        return (
          <code key={key} style={{ fontFamily: "monospace" }}>
            {node.content}
          </code>
        );
      case "link":
        return (
          <a key={key} href={node.url} target="_blank" rel="noreferrer">
            {node.content}
          </a>
        );
      default:
        return <React.Fragment key={key}>{node.content}</React.Fragment>;
    }
  });
};

const renderInlineContent = (text, keyPrefix) => {
  if (!text) return null;
  const lines = text.split(/\n/);
  const fragments = [];
  lines.forEach((line, idx) => {
    fragments.push(...tokenizeInline(line, `${keyPrefix}-l${idx}`));
    if (idx < lines.length - 1) {
      fragments.push(<br key={`${keyPrefix}-br-${idx}`} />);
    }
  });
  return fragments;
};

const parseMarkdownBlocks = (input) => {
  if (!input) return [];
  const lines = input.replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let paragraphLines = [];
  let listItems = [];
  let inCode = false;
  let codeLang = "";
  let codeLines = [];

  const flushParagraph = () => {
    if (paragraphLines.length === 0) return;
    const content = paragraphLines.join("\n").trim();
    if (content) {
      blocks.push({ type: "paragraph", content });
    }
    paragraphLines = [];
  };

  const flushList = () => {
    if (listItems.length === 0) return;
    blocks.push({ type: "list", items: listItems.slice() });
    listItems = [];
  };

  const flushCode = () => {
    if (!inCode) return;
    blocks.push({ type: "code", content: codeLines.join("\n"), language: codeLang });
    codeLines = [];
    codeLang = "";
    inCode = false;
  };

  for (const rawLine of lines) {
    const line = rawLine;
    const trimmed = line.trim();

    if (trimmed.startsWith("```")) {
      if (inCode) {
        flushCode();
      } else {
        flushParagraph();
        flushList();
        inCode = true;
        codeLang = trimmed.slice(3).trim();
      }
      continue;
    }

    if (inCode) {
      codeLines.push(line);
      continue;
    }

    if (trimmed === "") {
      flushParagraph();
      flushList();
      continue;
    }

    const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      flushParagraph();
      flushList();
      blocks.push({
        type: "heading",
        depth: headingMatch[1].length,
        content: headingMatch[2],
      });
      continue;
    }

    const listMatch = trimmed.match(/^[-*+]\s+(.*)$/);
    if (listMatch) {
      flushParagraph();
      listItems.push(listMatch[1]);
      continue;
    }

    if (listItems.length) {
      flushList();
    }

    paragraphLines.push(line);
  }

  flushParagraph();
  flushList();
  if (inCode) {
    flushCode();
  }

  return blocks;
};

const MarkdownRenderer = ({ text }) => {
  const blocks = useMemo(() => parseMarkdownBlocks(text), [text]);
  if (!text) {
    return null;
  }
  return (
    <div className="ai-summary-markdown">
      {blocks.map((block, idx) => {
        const key = `md-block-${idx}`;
        if (block.type === "heading") {
          const HeadingTag = `h${Math.min(6, block.depth || 3)}`;
          return (
            <HeadingTag key={key} className="fw-semibold">
              {renderInlineContent(block.content, `${key}-heading`)}
            </HeadingTag>
          );
        }
        if (block.type === "list") {
          return (
            <ul key={key} className="ps-3 mb-2">
              {block.items.map((item, itemIdx) => (
                <li key={`${key}-item-${itemIdx}`}>
                  {renderInlineContent(item, `${key}-item-${itemIdx}`)}
                </li>
              ))}
            </ul>
          );
        }
        if (block.type === "code") {
          return (
            <pre key={key} className="p-2 rounded-3 bg-dark-subtle bg-opacity-25">
              <code>{block.content}</code>
            </pre>
          );
        }
        return (
          <p key={key} className="mb-2">
            {renderInlineContent(block.content, `${key}-paragraph`)}
          </p>
        );
      })}
    </div>
  );
};

const MatchingNews = () => {
    const placeholderImage = NEWS_PLACEHOLDER_URL;
    const [loading, setLoading] = useState(true); // start true so spinner shows on hard reload; will be flipped off when no query or after fetch
  const [viewMode, setViewMode] = useState("list");
  const { t } = useTranslation();
  const breakpoint = useBreakpoint();
  const [searchParams] = useSearchParams();
  const isAI = searchParams.get("type") === "ai";

    // For async branches polling
  const [branchesJobKey, setBranchesJobKey] = useState(null);
  const [branchesJobStatus, setBranchesJobStatus] = useState(null); // "pending" | "ready" | "not_found" | null
  const pollAbortRef = React.useRef(null);
  const pollTimeoutRef = React.useRef(null);

  const q = useMemo(() => (searchParams.get("q") || "").trim(), [searchParams]);
      useEffect(() => {
    if (q) setLoading(true);
    // cancel any previous polling when query changes
    if (pollAbortRef.current) {
      try { pollAbortRef.current.abort(); } catch {}
      pollAbortRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    setBranchesJobKey(null);
    setBranchesJobStatus(null);
  }, [q]);

  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [branches, setBranches] = useState([]);
  const [aiSummary, setAiSummary] = useState("");

    const startPollingBranches = (key) => {
    if (!key) return;
    // ensure previous poll is cancelled
    if (pollAbortRef.current) {
      try { pollAbortRef.current.abort(); } catch {}
      pollAbortRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    setBranchesJobKey(key);
    setBranchesJobStatus("pending");

    const pollOnce = async () => {
      const aborter = new AbortController();
      pollAbortRef.current = aborter;
      try {
        const data = await getNewsBranches({ key, signal: aborter.signal });
        const status = data?.status || "pending";
        setBranchesJobStatus(status);
        if (status === "ready") {
          const branchList = Array.isArray(data?.branches) ? data.branches : [];
          const projectedBranches = branchList.map((b, idx) => ({
            key: `${b.title || 'branch'}-${idx}`,
            title: b.title || t("news.branches.fallbackTitle"),
            summary: b.summary || "",
            sectors: Array.isArray(b.sectors) ? b.sectors.slice(0, 2) : [],
          }));
          setBranches(projectedBranches);
          // stop polling
          if (pollAbortRef.current) { try { pollAbortRef.current.abort(); } catch {} }
          pollAbortRef.current = null;
          return;
        }
        if (status === "not_found") {
          // give up silently
          return;
        }
        // schedule next attempt
        pollTimeoutRef.current = setTimeout(pollOnce, 1500);
      } catch (e) {
        if (e.name === "AbortError") return; // ignore
        // backoff and retry
        pollTimeoutRef.current = setTimeout(pollOnce, 2000);
      }
    };

    // start first poll
    pollOnce();
  };

    // Unified fetch for both AI and non-AI modes; shows loader immediately on query change
  useEffect(() => {
    const controller = new AbortController();

    const run = async () => {
      const query = q;
      // If no query, clear and stop; also make sure loading is false
      if (!query) {
        setItems([]);
        setBranches([]);
        setAiSummary("");
        setLoading(false);
        return;
      }

            // This is the key change: When a new search starts,
      // immediately set loading to true AND clear all previous state.
      // This guarantees a visual update.
      setLoading(true);
      setError("");
      setItems([]);
      setBranches([]);
      setAiSummary("");
      await new Promise((r) => requestAnimationFrame(() => r()));

      try {
        setLoading(true);
        setError("");
        const uid = getUserId();
        const json = await searchNews({
          query,
          limit: 20,
          includeBranches: true,
          ai: isAI,
          userId: uid || undefined,
          language: getSelectedLanguage(),
          signal: controller.signal,
        });

        const resultList = Array.isArray(json) ? json : (json?.results || []);
        const branchList = Array.isArray(json) ? [] : (json?.branches || []);
        const aiTxt = Array.isArray(json) ? "" : (json?.ai_summary || "");

        const branchesJob = Array.isArray(json) ? null : (json?.branches_job || null);

        const projected = resultList.map((r) => {
          const rid = r._id ?? r.id;
          return {
            title: r.title,
            summary: r.summary,
            id: rid,
            display_article_id: rid,
            match_rate: r.progress ?? 0,
            image_url: r.image_url,
            sectors: Array.isArray(r.sectors) ? r.sectors : [],
            recent_at: r.created_at,
            reading_time_minutes: r.reading_time_minutes ?? null,
          };
        });
        setItems(projected);

                const projectedBranches = branchList.map((b, idx) => ({
          key: `${b.title || 'branch'}-${idx}`,
            title: b.title || t("news.branches.fallbackTitle"),
          summary: b.summary || "",
          sectors: Array.isArray(b.sectors) ? b.sectors.slice(0, 2) : [],
        }));
        setBranches(projectedBranches);
        setAiSummary(aiTxt);

        // Kick off polling if server says pending and we currently have no branches
        if ((!projectedBranches || projectedBranches.length === 0) && branchesJob && branchesJob.status === "pending" && branchesJob.key) {
          startPollingBranches(branchesJob.key);
        } else if (branchesJob && branchesJob.status === "ready" && branchesJob.key && projectedBranches.length === 0) {
          // Edge-case: server had cached branches; proactively poll once to fetch them
          startPollingBranches(branchesJob.key);
        } else {
          // no polling needed
          setBranchesJobKey(branchesJob?.key || null);
          setBranchesJobStatus(branchesJob?.status || null);
        }
      } catch (e) {
        if (e.name !== "AbortError") setError(e.message || t("common.error.unknown"));
      } finally {
        setLoading(false);
      }
    };

    // Kick off immediately so the overlay appears on first mount with a query
    run();

    return () => controller.abort();
  }, [q, isAI]);
  
  return (
    <div>
      <div className="main__content__box">
        <div className="container-fluid px-0">
          <div className="row g-4">
            {/* Left Side Content */}
            <div className={`${["tablet", "mobile"].includes(breakpoint) ? "col-md-12" : "col-md-8"}`}>
                          {isAI && (
              <div className="question_wrap">
                <h5>{q}</h5>
                {aiSummary ? (
                  <MarkdownRenderer text={aiSummary} />
                ) : (
                  <p>{t("news.ai.analyzing")}</p>
                )}
              </div>
            )}

              <div
                className={`matching_heading_box d-flex align-items-center justify-content-between my-4 ${
                  !isAI ? "no-margin-top" : ""
                }`}
              >
                <h4 className="title text-white fw-bold mb-0">{t("news.matching.title")}</h4>
                {!["tablet", "mobile"].includes(breakpoint) && (
                  <div className="view-toggle d-flex gap-3">
                    <button
                      className={`toggle_btn ${viewMode === "list" ? "toggle-active" : ""}`}
                      onClick={() => setViewMode("list")}
                    >
                      <FaList />
                    </button>
                    <button
                      className={`toggle_btn ${viewMode === "grid" ? "toggle-active" : ""}`}
                      onClick={() => setViewMode("grid")}
                    >
                      <FaThLarge />
                    </button>
                  </div>
                )}
              </div>

              {/* Loading / Error */}
              {!isAI && !loading && error && (
                <div className="alert alert-danger py-2">{error}</div>
              )}

              {/* News Cards */}
              <div className="row g-4 ">
                {loading ? (
                  Array.from({ length: viewMode === "list" ? 4 : 6 }).map((_, index) => (
                    <div key={`skeleton-${index}`} className={viewMode === "list" ? "col-12" : "col-md-6"}>
                      <NewsCardSkeleton viewMode={viewMode} />
                    </div>
                  ))
                ) : items.length > 0 ? (
  items.map((item, idx) => (
    <div key={`${item.display_article_id || item.id}-${idx}`} className={viewMode === "list" ? "col-12" : "col-md-6"}>
      <NewsCard
        value={idx + 1}
        viewMode={viewMode}
        title={item.title || item.seo_title || t("news.untitled")}
        summary={item.summary || (item.llm_output && item.llm_output.summary) || ""}
        _id={item.id || item.display_article_id}
        progress={item.match_rate || item.progress || 0}
        image_url={item.image_url || placeholderImage}
        sectors={item.sectors || []}
        created_at={item.recent_at || item.created_at}
        reading_time_minutes={item.reading_time_minutes}
      />
    </div>
  ))
) : (
  <div className="col-12 text-white-50">{t("news.search.noResults")}</div>
)}
              </div>
            </div>

                        {breakpoint === "laptop" && (
              <div className="col-md-4">
                <div className="news_comments">
                  <div className="heading_box">
                    <h5 className="text-uppercase fw-bold text-white text-center pt-2 mb-0">{t("news.branches.title")}</h5>
                  </div>
                  {loading && Array.from({ length: 3 }).map((_, idx) => (
                    <div className="mt-4" key={`branch-skeleton-${idx}`}>
                      <AllNewsSkeleton />
                    </div>
                  ))}
                                    {(branches.length === 0 && !loading) && (
                    <div className="text-white-50 mt-3 text-center">
                      {branchesJobStatus === "pending" ? t("news.branches.loading") : t("news.branches.none")}
                    </div>
                  )}
                  {branches.map((b) => (
                    <div className="mt-4" key={b.key}>
                      <AllNews title={b.title} summary={b.summary} sectors={b.sectors} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchingNews;
